import React, { useEffect, useState, useMemo } from 'react'
import {
	Users as UsersIcon,
	UserPlus,
	Eye,
	Edit2,
	Trash2,
	RotateCcw,
	UserX,
	UserCheck,
	Key,
	AlertCircle,
	XCircle,
	Filter,
	Search,
	Loader2,
} from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader, Alert, ResetPasswordModal } from '@components/common'
import TeamMemberForm from './TeamMemberForm'
import TeamMemberView from './TeamMemberView'
import { userService } from '@services/userService'
import { roleService } from '@services/roleService'
import toast from 'react-hot-toast'
import './TeamMembers.scss'

/**
 * Get user initials from name
 */
const getInitials = (firstName, lastName) => {
	const first = firstName?.charAt(0)?.toUpperCase() || ''
	const last = lastName?.charAt(0)?.toUpperCase() || ''
	return first + last || '?'
}

/**
 * Format last login timestamp
 */
const formatLastLogin = (timestamp) => {
	if (!timestamp) return 'Never'

	const now = new Date()
	const loginDate = new Date(timestamp)
	const diffInSeconds = Math.floor((now - loginDate) / 1000)

	if (diffInSeconds < 60) return 'Just now'
	if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
	if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
	if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`

	return loginDate.toLocaleDateString()
}

/**
 * Team Members Management Page (Company Admin)
 *
 * User management interface for company admins to manage their team members.
 * - Only shows users from the company admin's company
 * - Role selection excludes super-admin role
 * - Includes system roles + company custom roles
 */
const TeamMembers = () => {
	// State
	const [users, setUsers] = useState([])
	const [roles, setRoles] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [selectedUser, setSelectedUser] = useState(null)

	// Filters
	const [searchTerm, setSearchTerm] = useState('')
	const [roleFilter, setRoleFilter] = useState('all')
	const [statusFilter, setStatusFilter] = useState('all')

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
	const [actionType, setActionType] = useState(null)
	const [selectedUserForAction, setSelectedUserForAction] = useState(null)
	const [resetPasswordLoading, setResetPasswordLoading] = useState(false)

	// Fetch users and roles on mount
	useEffect(() => {
		fetchUsers()
		fetchRoles()
	}, [])

	/**
	 * Fetch users for the company
	 * SECURITY: company_id is automatically filtered by backend using authenticated user's company
	 */
	const fetchUsers = async () => {
		setLoading(true)
		setError(null)
		try {
			// No need to pass company_id - backend filters automatically
			const response = await userService.getAll()
			setUsers(response.data || [])
		} catch (err) {
			console.error('Error fetching users:', err)
			setError('Failed to load team members')
			toast.error('Failed to load team members')
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Fetch assignable roles for the company (excludes super-admin)
	 * SECURITY: Backend filters roles automatically based on authenticated user's company
	 */
	const fetchRoles = async () => {
		try {
			// No need to pass companyId - backend filters automatically
			const response = await roleService.getCompanyAssignableRoles()
			setRoles(response.data || [])
		} catch (err) {
			console.error('Error fetching roles:', err)
			toast.error('Failed to load roles')
		}
	}

	// Filter users based on search and filters
	const filteredUsers = useMemo(() => {
		return users.filter((user) => {
			// Search filter
			if (searchTerm) {
				const search = searchTerm.toLowerCase()
				const fullName = `${user.first_name} ${user.last_name}`.toLowerCase()
				const email = user.email?.toLowerCase() || ''
				if (!fullName.includes(search) && !email.includes(search)) {
					return false
				}
			}

			// Role filter
			if (roleFilter !== 'all' && user.role_id != roleFilter) {
				return false
			}

			// Status filter
			if (statusFilter !== 'all' && user.status !== statusFilter) {
				return false
			}

			return true
		})
	}, [users, searchTerm, roleFilter, statusFilter])

	// Calculate stats
	const stats = useMemo(() => {
		return {
			total: users.length,
			active: users.filter((u) => u.status === 'active').length,
			inactive: users.filter((u) => u.status === 'inactive').length,
			suspended: users.filter((u) => u.status === 'suspended').length,
		}
	}, [users])

	/**
	 * Handle create user
	 */
	const handleCreate = () => {
		setSelectedUser(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view user
	 */
	const handleView = (user) => {
		setSelectedUser(user)
		setShowViewModal(true)
	}

	/**
	 * Handle edit user
	 */
	const handleEdit = (user) => {
		setSelectedUser(user)
		setShowEditModal(true)
	}

	/**
	 * Handle delete user
	 */
	const handleDelete = (user) => {
		setSelectedUserForAction(user)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore user
	 */
	const handleRestore = (user) => {
		setSelectedUserForAction(user)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle block user
	 */
	const handleBlock = (user) => {
		setSelectedUserForAction(user)
		setActionType('block')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle unblock user
	 */
	const handleUnblock = (user) => {
		setSelectedUserForAction(user)
		setActionType('unblock')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle reset password - opens modal for manual password entry
	 */
	const handleResetPassword = (user) => {
		setSelectedUserForAction(user)
		setShowResetPasswordModal(true)
	}

	/**
	 * Submit password reset with manually entered password
	 */
	const handleResetPasswordSubmit = async (newPassword) => {
		if (!selectedUserForAction) return

		setResetPasswordLoading(true)
		try {
			await userService.resetPassword(selectedUserForAction.id, newPassword)
			toast.success('Password reset successfully')
			setShowResetPasswordModal(false)
			setSelectedUserForAction(null)
		} catch (error) {
			console.error('Password reset failed:', error)
			toast.error(error.message || 'Failed to reset password')
		} finally {
			setResetPasswordLoading(false)
		}
	}

	/**
	 * Confirm action (delete/restore/block/unblock)
	 */
	const confirmAction = async () => {
		if (!selectedUserForAction) return

		try {
			if (actionType === 'delete') {
				await userService.delete(selectedUserForAction.id)
				toast.success('Team member deleted successfully')
			} else if (actionType === 'restore') {
				await userService.restore(selectedUserForAction.id)
				toast.success('Team member restored successfully')
			} else if (actionType === 'block') {
				await userService.updateStatus(selectedUserForAction.id, 'suspended')
				toast.success('Team member blocked successfully')
			} else if (actionType === 'unblock') {
				await userService.updateStatus(selectedUserForAction.id, 'active')
				toast.success('Team member unblocked successfully')
			}

			// Refresh users
			fetchUsers()

			setShowConfirmDialog(false)
			setSelectedUserForAction(null)
			setActionType(null)
		} catch (error) {
			console.error('Action failed:', error)
			toast.error(error.message || 'Action failed')
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedUserForAction(null)
		setActionType(null)
	}

	/**
	 * Handle form success (create/edit)
	 */
	const handleFormSuccess = () => {
		setShowCreateModal(false)
		setShowEditModal(false)
		fetchUsers()
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status) => {
		if (status === 'active') {
			return <span className="badge badge--success">Active</span>
		} else if (status === 'suspended') {
			return <span className="badge badge--danger">Suspended</span>
		}
		return <span className="badge badge--secondary">Inactive</span>
	}

	/**
	 * Get role badge
	 */
	const getRoleBadge = (roleSlug, roleName) => {
		let badgeClass = 'badge'

		if (roleSlug === 'company-admin') {
			badgeClass += ' badge--primary'
		} else if (roleSlug === 'compliance-manager') {
			badgeClass += ' badge--info'
		} else if (roleSlug === 'auditor') {
			badgeClass += ' badge--warning'
		} else if (roleSlug === 'project-manager') {
			badgeClass += ' badge--success'
		} else {
			badgeClass += ' badge--secondary'
		}

		return <span className={badgeClass}>{roleName || 'Unknown'}</span>
	}

	/**
	 * Get action confirmation details
	 */
	const getConfirmDetails = () => {
		if (!selectedUserForAction || !actionType) return {}

		const userName = `${selectedUserForAction.first_name} ${selectedUserForAction.last_name}`

		const details = {
			delete: {
				title: 'Delete Team Member?',
				message: `Are you sure you want to delete "${userName}"? This will deactivate the user account.`,
				confirmText: 'Delete',
				variant: 'danger',
			},
			restore: {
				title: 'Restore Team Member?',
				message: `Are you sure you want to restore "${userName}"? This will reactivate the user account.`,
				confirmText: 'Restore',
				variant: 'info',
			},
			block: {
				title: 'Block Team Member?',
				message: `Are you sure you want to block "${userName}"? This will suspend the user account and prevent login.`,
				confirmText: 'Block',
				variant: 'warning',
			},
			unblock: {
				title: 'Unblock Team Member?',
				message: `Are you sure you want to unblock "${userName}"? This will restore access to the user account.`,
				confirmText: 'Unblock',
				variant: 'success',
			},
		}

		return details[actionType] || {}
	}

	// Define table columns
	const columns = [
		{
			key: 'avatar',
			label: '',
			align: 'center',
			render: (user) => (
				<div className="team-member-avatar">
					{user.avatar_url ? (
						<img
							src={user.avatar_url}
							alt={`${user.first_name} ${user.last_name}`}
							className="team-member-avatar__image"
						/>
					) : (
						<div className="team-member-avatar__initials">{getInitials(user.first_name, user.last_name)}</div>
					)}
				</div>
			),
		},
		{
			key: 'name',
			label: 'Name',
			sortable: true,
			render: (user) => (
				<div className="team-member-cell">
					<div className="team-member-cell__name">{`${user.first_name} ${user.last_name}`}</div>
					<div className="team-member-cell__email">{user.email}</div>
				</div>
			),
		},
		{
			key: 'role',
			label: 'Role',
			sortable: true,
			align: 'center',
			render: (user) => getRoleBadge(user.role_slug, user.role_name),
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (user) => getStatusBadge(user.status),
		},
		{
			key: 'last_login',
			label: 'Last Login',
			sortable: true,
			render: (user) => <span className="last-login-cell">{formatLastLogin(user.last_login_at)}</span>,
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (user) => user.status !== 'suspended',
		},
		{
			label: 'Block User',
			icon: <UserX size={16} />,
			onClick: handleBlock,
			variant: 'warning',
			show: (user) => user.status !== 'suspended',
		},
		{
			label: 'Unblock User',
			icon: <UserCheck size={16} />,
			onClick: handleUnblock,
			variant: 'success',
			show: (user) => user.status === 'suspended',
		},
		{
			label: 'Reset Password',
			icon: <Key size={16} />,
			onClick: handleResetPassword,
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (user) => !user.deleted_at,
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (user) => user.deleted_at,
		},
	]

	const confirmDetails = getConfirmDetails()

	return (
		<div className="team-members-page">
			{/* Page Header */}
			<PageHeader
				title="Team Members"
				description="Manage your team members and their roles"
				breadcrumbs={[{ label: 'Team' }, { label: 'Team Members' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<UserPlus size={20} />
						<span>Add Team Member</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<UsersIcon size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Members</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<UserCheck size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<AlertCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Inactive</div>
						<div className="stat-card__value">{stats.inactive}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--danger">
						<XCircle size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Suspended</div>
						<div className="stat-card__value">{stats.suspended}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={searchTerm}
					onChange={setSearchTerm}
					placeholder="Search by name or email..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={roleFilter}
						onChange={(e) => setRoleFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Roles' },
							...roles.map((role) => ({
								value: role.id,
								label: role.name,
							})),
						]}
					/>

					<Select
						value={statusFilter}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'inactive', label: 'Inactive' },
							{ value: 'suspended', label: 'Suspended' },
						]}
					/>
				</div>
			</div>

			{/* Error State */}
			{error && <Alert variant="error" message={error} className="mb-4" />}

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={filteredUsers}
				actions={actions}
				loading={loading}
				emptyMessage="No team members found. Add your first team member to get started."
				onRowClick={handleView}
			/>

			{/* Create Modal */}
			{showCreateModal && (
				<TeamMemberForm
					mode="create"
					roles={roles}
					onClose={() => setShowCreateModal(false)}
					onSuccess={handleFormSuccess}
				/>
			)}

			{/* Edit Modal */}
			{showEditModal && selectedUser && (
				<TeamMemberForm
					mode="edit"
					roles={roles}
					user={selectedUser}
					onClose={() => setShowEditModal(false)}
					onSuccess={handleFormSuccess}
				/>
			)}

			{/* View Modal */}
			{showViewModal && selectedUser && (
				<TeamMemberView
					user={selectedUser}
					onClose={() => setShowViewModal(false)}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
				/>
			)}

			{/* Confirm Dialog */}
			{showConfirmDialog && selectedUserForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={confirmDetails.title}
					message={confirmDetails.message}
					confirmText={confirmDetails.confirmText}
					variant={confirmDetails.variant}
				/>
			)}

			{/* Reset Password Modal */}
			<ResetPasswordModal
				isOpen={showResetPasswordModal}
				onClose={() => {
					setShowResetPasswordModal(false)
					setSelectedUserForAction(null)
				}}
				onSubmit={handleResetPasswordSubmit}
				user={selectedUserForAction}
				loading={resetPasswordLoading}
			/>
		</div>
	)
}

export default TeamMembers
